import Toast from 'react-native-toast-message';
import type {IReactionDisposer} from 'mobx';
import {makeAutoObservable, onBecomeObserved, reaction, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE} from '../components/ToastBottom';
import apiService from '../transport/apiService';
import {ApplyOfferSuccessResponse, OffersItemWithMflId, SignUpOfferData} from '../transport/types';
import {logError} from '../utils/errorLogging';
import {logger, serializeError} from '../utils/logger';
import {ApplyOfferError, parseApplyOfferError, shouldAutoRetry} from '../utils/offerErrorHandler';

import type {RootStore} from './rootStore';

/**
 * Retry configuration for offer apply API calls.
 *
 * MAX_RETRIES: Total retry attempts (not counting initial request)
 * RETRY_DELAYS: Delay (ms) before each retry using exponential backoff
 *
 * Worst-case timeline: 3 attempts over 3 seconds
 * - Initial attempt: immediate
 * - Retry 1: after 1000ms
 * - Retry 2: after 2000ms (3000ms total from start)
 */
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000];

/**
 * Delays (ms) before each re-fetch after a member's offers load returned a
 * recoverable list (see `isRecoverable`) or failed. DIS-1910: MFL can return
 * 200 with an empty deals list for a live member session. Not verified: how
 * long such a spell lasts, so these delays are not fitted to a measured
 * recovery time.
 * SHORTCUT: the budget is these 3 retries per OffersStore instance.
 * `refreshOffers()`, the foreground re-fetch and the card-number load use the
 * retries that are left and do not restore them. Only a response with an
 * MFL-backed offer restores the budget, and `resetOffersStore()` starts a new
 * store with a full budget. Ceiling: a member at a shop with no MFL deals gets
 * at most 3 timer retries per store instance, and an unbroken chain ends 255 s
 * (15 + 60 + 180) after the first recoverable response. A longer spell relies
 * on the foreground re-fetch (`refreshOnForeground`) or screen focus. Upgrade
 * path: a middleware signal that says why the list is empty.
 */
const EMPTY_RETRY_DELAYS = [15_000, 60_000, 180_000];

/**
 * True when the list has no MFL-backed offer. Zen-sourced Coffee Card rows
 * carry no `mflId` and arrive even when MFL returned `[]`, so a coffee-card-only
 * list is as recoverable as an empty one. `null` (never loaded, or `reset()`)
 * is recoverable too.
 */
export const isRecoverable = (list: OffersItemWithMflId[] | null) =>
  !(list ?? []).some(o => o.mflId);

/** Minimum gap (ms) between two foreground re-fetches. */
const FOREGROUND_REFRESH_DEBOUNCE_MS = 60_000;

/** Load status of the member's offers list. */
export type OffersStatus = 'idle' | 'loading' | 'loaded' | 'error';

export class OfferDetailStore {
  private rootStore: RootStore;
  private offerId: string;
  private initialOffer: OffersItemWithMflId;

  linkedProducts: unknown[] | null = null;
  isLoadingLinkedProducts: boolean = false;
  isLoadingCoffeeCardStats: boolean = false;
  isApplyLoading: boolean = false;
  applyError: ApplyOfferError | null = null;

  /**
   * Live computed getter — always reads from offersStore.data so canApply,
   * disabledReason etc. stay in sync after refreshOffers() replaces the array.
   * Falls back to the initial snapshot if offer is removed from the list.
   */
  get offer(): OffersItemWithMflId {
    return (
      this.rootStore.offersStore.data?.find(o => o.offerId === this.offerId) || this.initialOffer
    );
  }

  constructor(offer: OffersItemWithMflId, rootStore: RootStore) {
    // DIS-1661: track the offer by contentId — mflId can be absent.
    this.offerId = offer.offerId;
    this.initialOffer = offer;
    this.rootStore = rootStore;

    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'linkedProducts', this.load);
  }

  private load = async () => {
    await this.loadLinkedProducts();
  };

  private loadLinkedProducts = async () => {
    if (!this.linkedProducts && this.offer?.barcodes?.length > 0) {
      runInAction(() => {
        this.isLoadingLinkedProducts = true;
      });
      apiService
        .searchLines({
          product_codes: this.offer.barcodes.flatMap(value =>
            value.split(';').map(barcode => barcode.trim()),
          ),
        })
        .then((lines: unknown) => {
          runInAction(() => {
            this.linkedProducts = (lines as unknown[]) || [];
            this.isLoadingLinkedProducts = false;
          });
        })
        .catch(e => {
          runInAction(() => {
            this.isLoadingLinkedProducts = false;
          });
          logError(e, 'OfferDetailStore.loadLinkedProducts');
        });
    }
  };

  clearApplyError() {
    this.applyError = null;
  }

  async applyOffer(): Promise<{success: boolean; error?: ApplyOfferError}> {
    runInAction(() => {
      this.isApplyLoading = true;
      this.applyError = null;
    });

    const mflId = this.offer.mflId;
    if (!mflId) {
      // DIS-1661: an offer with no MFL deal id has nothing to apply against.
      // The detail screen's APPLY button is disabled for it; this is the
      // defence in depth behind that.
      runInAction(() => {
        this.isApplyLoading = false;
      });
      logger.error('OfferDetailStore', 'Cannot apply an offer with no mflId', {
        offerId: this.offerId,
      });
      return {success: false};
    }

    try {
      const zenGlobalId = this.rootStore.rewardsStore.cardNumber || undefined;
      const response = await this.applyWithRetry(mflId, zenGlobalId);

      runInAction(() => {
        // Sync activated_deals directly from response — this is the single source of truth
        // for isApplied status across all offer screens (detail, list, compact tile)
        this.rootStore.cartStore.activatedDeals = response.data.activated_deals;
      });

      // Reload order to get fresh adjustments and totals reflecting the applied offer
      await this.rootStore.cartStore.loadOrderForUser();

      // DIS-1661 Task D3: without this, canApply/disabledReason/isFullyRedeemed
      // on the just-applied offer (and on any other offer) go stale — the
      // compact tile's inline apply already refreshes here (cartStore.applyOffer);
      // this is the detail screen's apply path, which was missing it.
      await this.rootStore.offersStore.refreshOffers();

      return {success: true};
    } catch (error) {
      const parsed = error as ApplyOfferError;

      runInAction(() => {
        this.applyError = parsed;
      });

      // Toast for terminal (non-recoverable) errors only
      if (!parsed.recoverable) {
        Toast.show({
          type: ERROR_MESSAGE,
          text1: parsed.message,
        });
      }

      return {success: false, error: parsed};
    } finally {
      runInAction(() => {
        this.isApplyLoading = false;
      });
    }
  }

  private async applyWithRetry(
    mflId: string,
    zenGlobalId?: string,
    attempt = 0,
  ): Promise<ApplyOfferSuccessResponse> {
    try {
      return await apiService.applyOffer(mflId, zenGlobalId);
    } catch (error) {
      const parsed = parseApplyOfferError(error);

      if (shouldAutoRetry(parsed) && attempt < MAX_RETRIES) {
        await this.sleep(RETRY_DELAYS[attempt]);
        return this.applyWithRetry(mflId, zenGlobalId, attempt + 1);
      }

      throw parsed;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export class OffersStore {
  private signUpOfferPromise: Promise<SignUpOfferData[]> | undefined;
  private rootStore: RootStore;

  isLoading: boolean = false;
  isLoadingSignUpOffers: boolean = false;

  signUpOfferData: SignUpOfferData[] | null = null;

  data: OffersItemWithMflId[] | null = null;

  /**
   * DIS-1914: additive load state. `isLoading` and `data` keep their meaning.
   * Both fields change in the same action as `isLoading`.
   */
  offersStatus: OffersStatus = 'idle';

  /** Cause of the latest failed load; `null` after a successful load. */
  lastLoadError: {status?: number; reason: string} | null = null;

  /** True once `load` has been called at least once. */
  private hasRequestedOffers = false;

  /** Monotonic id of the most recently started `load`. */
  private loadGeneration = 0;

  /** Disposer for the lazily-registered card-number reaction; also its guard. */
  private cardNumberReactionDisposer: IReactionDisposer | null = null;

  /** Pending re-fetch after an empty or failed member load. */
  private emptyRetryTimer: ReturnType<typeof setTimeout> | null = null;

  /** Timer retries already fired from this store's budget (`EMPTY_RETRY_DELAYS`). */
  private emptyRetriesUsed = 0;

  private lastForegroundRefreshAt = 0;

  /** True while an empty-list retry timer is pending. */
  get isRetryingEmpty(): boolean {
    return this.emptyRetryTimer !== null;
  }

  /** Retry budget minus retries fired and the one pending in this empty streak. */
  get emptyRetriesRemaining(): number {
    return EMPTY_RETRY_DELAYS.length - this.emptyRetriesUsed - (this.isRetryingEmpty ? 1 : 0);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily - requires authentication.
     *
     * Guarded so it fetches ONCE. `onBecomeObserved` fires on every transition
     * from unobserved to observed, not only the first, and a consumer can drop
     * its observation between renders without unmounting: in
     * `ExclusiveOfferListScreen/index.tsx` both branches reach `data` only
     * after a `||`/`&&` short-circuit on `isLoading`, so while a load is in
     * flight nothing reads `data` at all. Unguarded, the finished load
     * re-rendered the screen, the re-read re-armed this handler, and that
     * started the next load — measured 2026-09-17 on the SNS dev stack at 37-71
     * requests per 40s, one every ~1.4s, for as long as the screen stayed open.
     *
     * The loop only appeared when `rewardsStore` was cold, because a warm
     * rewards store keeps `ExclusiveOffers` mounted (CartContent.tsx:42) and
     * that component holds a continuous observation, so the count never reached
     * zero. Refreshes are explicit: `refreshOffers()` on cart change and screen
     * focus.
     */
    onBecomeObserved(this, 'data', () => {
      if (this.hasRequestedOffers) return;
      void this.load();
    });

    /**
     * Fetch sign-up offers lazily - public API, no authentication required
     */
    onBecomeObserved(this, 'signUpOfferData', this.loadSignUpOffers);
  }

  /**
   * DIS-1661: re-fetch once the member's Zen card number arrives.
   *
   * The coffee-card pass on the offers endpoint runs only when the request
   * carries the card number, which `load` reads from rewardsStore. On a cold
   * start rewardsStore is empty at first paint and is populated by
   * /rewards/account-details. Measured 2026-09-17 against the SNS dev stack:
   * the offers request went out at 00:43:04.750 and account-details resolved at
   * 00:43:09.316 — 4.6s later — so the offers call carried no card number and
   * the coffee card was absent. Nothing re-fetched afterwards, so the card
   * stayed missing for the whole session and its detail screen rendered
   * "THIS EXCLUSIVE OFFER HAS FINISHED".
   *
   * Registered lazily rather than in the constructor: rootStore.ts:125 builds
   * OffersStore BEFORE rootStore.ts:129 builds RewardsStore, so at construction
   * `this.rootStore.rewardsStore` is undefined and the reaction's data function
   * throws at setup, leaving a dead reaction that never fires. Measured
   * 2026-09-17: with the reaction in the constructor the cold-start run made
   * exactly one offers call and never re-fetched. By the time `load` first
   * runs, every store exists.
   */
  private ensureCardNumberReaction = () => {
    if (this.cardNumberReactionDisposer) return;
    this.cardNumberReactionDisposer = reaction(
      () => this.rootStore.rewardsStore?.cardNumber,
      cardNumber => {
        if (!cardNumber) return;
        void this.load();
      },
    );
  };

  private loadSignUpOffers = async () => {
    if (!this.signUpOfferPromise) {
      runInAction(() => {
        this.isLoadingSignUpOffers = true;
      });

      this.signUpOfferPromise = apiService.getSignUpOffers();

      try {
        const signUpOffers = await this.signUpOfferPromise;
        runInAction(() => {
          this.signUpOfferData = signUpOffers;
        });
      } catch (error) {
        logError(error, 'OffersStore.loadSignUpOffers');
        runInAction(() => {
          this.signUpOfferData = [];
        });
      } finally {
        runInAction(() => {
          this.isLoadingSignUpOffers = false;
        });
      }
    }
  };

  /**
   * `keepListOnFailure`: the retry timer and the foreground re-fetch pass true,
   * so a failed request keeps a list that is already shown. Other loads replace
   * it with `[]` on failure.
   */
  private load = async (keepListOnFailure = false) => {
    this.ensureCardNumberReaction();
    this.cancelEmptyRetry();
    this.hasRequestedOffers = true;
    // Identifies this request among concurrent loads. The card-number reaction
    // can fire while an earlier, card-less load is still in flight; without a
    // generation check the two responses race and the older one can overwrite
    // the newer, dropping the coffee card again.
    const generation = ++this.loadGeneration;

    runInAction(() => {
      this.isLoading = true;
      this.offersStatus = 'loading';
    });

    const storeNumber = this.rootStore.shopSelectionStore.selectedStore?.store_number;
    const zenCardNumber = this.rootStore.rewardsStore.cardNumber || undefined;

    let outcome: 'loaded' | 'error' = 'error';
    let loadError: {status?: number; reason: string} | null = null;

    try {
      const offers = await apiService.getOffersV2(storeNumber, zenCardNumber);
      if (generation !== this.loadGeneration) return;
      if (isRecoverable(offers)) {
        this.scheduleEmptyRetry();
      } else {
        this.emptyRetriesUsed = 0;
      }
      runInAction(() => {
        this.data = offers;

        // Sync activatedDeals from offers API isActivated field.
        // MFL order response does not include activated_deals, so cartStore
        // loses track of applied deals after page refresh. The offers API
        // isActivated field is the reliable server signal.
        const cartStore = this.rootStore.cartStore;
        const serverActivated = offers.flatMap(o => (o.isActivated && o.mflId ? [o.mflId] : []));
        const clientDeals = cartStore.activatedDeals;
        const needsSync =
          clientDeals.length !== serverActivated.length ||
          clientDeals.some(id => !serverActivated.includes(id));
        if (needsSync) {
          cartStore.activatedDeals = serverActivated;
        }
      });
      outcome = 'loaded';
    } catch (err) {
      if (generation !== this.loadGeneration) return;
      const status = (err as {response?: {status?: unknown}} | null)?.response?.status;
      loadError = {
        ...(typeof status === 'number' ? {status} : {}),
        reason: (err instanceof Error && err.message) || 'Unknown error',
      };
      logger.error('OffersStore', 'Error loading offers from v2 endpoint', serializeError(err));
      if (!(keepListOnFailure && this.data)) {
        runInAction(() => {
          this.data = []; // Graceful degradation - empty list, not broken state
        });
      }
      this.scheduleEmptyRetry();
    } finally {
      if (generation === this.loadGeneration) {
        runInAction(() => {
          this.isLoading = false;
          this.offersStatus = outcome;
          this.lastLoadError = loadError;
        });
      }
    }
  };

  /**
   * A member's empty list can be a transient MFL response, so re-fetch with
   * backoff. A guest's empty list is final. With no store selected,
   * `getOffersV2` returns `[]` without a request, so a retry cannot help.
   *
   * Only the current `rootStore.offersStore` retries. `resetOffersStore()`
   * replaces this store on opt-out, store switch and checkout, and calls
   * `dispose()` first. This check guards a replacement that skips
   * `dispose()`: that store's in-flight load can still resolve, and its timer
   * would otherwise fetch and overwrite the shared `cartStore.activatedDeals`.
   */
  private scheduleEmptyRetry = () => {
    this.cancelEmptyRetry();
    if (this.rootStore.offersStore !== this) return;
    if (this.emptyRetriesUsed >= EMPTY_RETRY_DELAYS.length) return;
    if (!this.rootStore.userStore?.isLoggedIn) return;
    if (!this.rootStore.shopSelectionStore.selectedStore?.store_number) return;
    this.emptyRetryTimer = setTimeout(() => {
      this.emptyRetryTimer = null;
      if (this.rootStore.offersStore !== this) return;
      if (!this.rootStore.userStore?.isLoggedIn) return;
      this.emptyRetriesUsed++;
      void this.load(true);
    }, EMPTY_RETRY_DELAYS[this.emptyRetriesUsed]);
  };

  private cancelEmptyRetry = () => {
    if (this.emptyRetryTimer) {
      clearTimeout(this.emptyRetryTimer);
      this.emptyRetryTimer = null;
    }
  };

  /**
   * Stops every source of a later fetch from this store: the retry timer and
   * the card-number reaction. Advancing `loadGeneration` also stops a load
   * already in flight from writing `data` or `cartStore.activatedDeals`.
   * `rootStore.resetOffersStore()` calls it before replacing the store.
   */
  dispose = () => {
    this.loadGeneration++;
    this.cancelEmptyRetry();
    this.cardNumberReactionDisposer?.();
    this.cardNumberReactionDisposer = null;
  };

  /**
   * App returned to the foreground (rootStore AppState listener). Re-fetches
   * only a list that was already requested, so it never triggers the lazy
   * first load, and only a recoverable list: a list with an MFL-backed offer
   * is left alone. The current list stays visible while the request is in
   * flight, and also when the request fails. A return while a load is in
   * flight starts no fetch and leaves the debounce slot unused.
   */
  refreshOnForeground = () => {
    if (
      this.isLoading ||
      !this.hasRequestedOffers ||
      !this.rootStore.userStore?.isLoggedIn ||
      !isRecoverable(this.data)
    ) {
      return Promise.resolve();
    }
    const now = Date.now();
    if (now - this.lastForegroundRefreshAt < FOREGROUND_REFRESH_DEBOUNCE_MS) {
      return Promise.resolve();
    }
    this.lastForegroundRefreshAt = now;
    return this.load(true);
  };

  /** Re-fetch offers to get fresh canApply/disabledReason after cart changes.
   * Guards against concurrent calls — rapid cart mutations (quantity changes,
   * adding items) can trigger multiple updateOrder completions in quick succession.
   * IMPORTANT: Must return the promise — callers (removeActivatedDeal) await it.
   */
  refreshOffers = () => {
    if (this.isLoading) return Promise.resolve();
    return this.load();
  };

  reset = () => {
    this.cancelEmptyRetry();
    runInAction(() => {
      this.data = null;
    });
  };

  resetSignUpOffers = () => {
    runInAction(() => {
      this.signUpOfferData = null;
      delete this.signUpOfferPromise;
    });
  };
}
